/**
 * Keeps the CSS [data-tip] hover labels (styles/app.css) inside their box. The label is a centred
 * ::after, so on the first/last tag in a row it pokes past the edge of a card with overflow:hidden
 * (or the window) and gets cut off. On hover/focus we measure the label and the nearest clipping
 * ancestor, then set --tip-shift so the label slides back inside. Pure CSS can't know where the edge is.
 */
const GAP = 8;

function clipBox(el: Element): { left: number; right: number } {
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const ox = getComputedStyle(p).overflowX;
    if (ox !== "visible") {
      const r = p.getBoundingClientRect();
      return { left: Math.max(r.left, 0), right: Math.min(r.right, window.innerWidth) };
    }
  }
  return { left: 0, right: window.innerWidth };
}

function place(target: HTMLElement): void {
  const pos = target.getAttribute("data-tip-pos");
  if (pos === "left" || pos === "right") return;
  target.style.removeProperty("--tip-shift");
  const width = parseFloat(getComputedStyle(target, "::after").width);
  if (!Number.isFinite(width) || width <= 0) return;
  const r = target.getBoundingClientRect();
  const box = clipBox(target);
  const centre = r.left + r.width / 2;
  const left = centre - width / 2;
  const right = centre + width / 2;
  let shift = 0;
  if (left < box.left + GAP) shift = box.left + GAP - left;
  else if (right > box.right - GAP) shift = box.right - GAP - right;
  if (shift) target.style.setProperty("--tip-shift", `${Math.round(shift)}px`);
}

function onEnter(e: Event): void {
  const t = (e.target as Element | null)?.closest?.("[data-tip]");
  if (t instanceof HTMLElement) place(t);
}

export function installTooltipEdges(): void {
  document.addEventListener("pointerover", onEnter, { passive: true });
  document.addEventListener("focusin", onEnter);
}
